// Referral tracking utility for PartnerStack/GrowSumo integration
// Captures referral parameters from URL and stores them for signup attribution

export interface ReferralData {
  referralKey?: string;
  partnerId?: string;
  campaignId?: string;
  source?: string;
  medium?: string;
  campaign?: string;
  timestamp?: string;
}

// Common PartnerStack/GrowSumo URL parameters
const REFERRAL_PARAMS = [
  'ps_partner_key',     // PartnerStack partner key
  'ps_campaign',        // PartnerStack campaign
  'growsumo_key',       // GrowSumo referral key
  'ref',                // Generic referral parameter
  'referral',           // Generic referral parameter
  'partner',            // Partner ID
  'campaign',           // Campaign ID
  'utm_source',         // UTM source
  'utm_medium',         // UTM medium
  'utm_campaign',       // UTM campaign
  'utm_content',        // UTM content
  'utm_term'            // UTM term
];

class ReferralTracker {
  private readonly STORAGE_KEY = 'referral_data';
  private readonly EXPIRY_DAYS = 30; // Referral attribution window

  /**
   * Capture referral data from current URL parameters
   */
  captureReferralData(): ReferralData | null {
    if (typeof window === 'undefined') {
      console.log('🔗 Window undefined - server-side rendering');
      return null;
    }

    const urlParams = new URLSearchParams(window.location.search);
    console.log('🔗 Checking URL parameters:', Array.from(urlParams.entries()));

    const referralData: ReferralData = {};
    let hasReferralData = false;

    // Check for PartnerStack/GrowSumo specific parameters
    if (urlParams.has('ps_partner_key')) {
      const value = urlParams.get('ps_partner_key');
      console.log('🔗 Found ps_partner_key:', value);
      referralData.partnerId = value || undefined;
      hasReferralData = true;
    }

    if (urlParams.has('ps_campaign')) {
      const value = urlParams.get('ps_campaign');
      console.log('🔗 Found ps_campaign:', value);
      referralData.campaignId = value || undefined;
      hasReferralData = true;
    }

    if (urlParams.has('growsumo_key')) {
      const value = urlParams.get('growsumo_key');
      console.log('🔗 Found growsumo_key:', value);
      referralData.referralKey = value || undefined;
      hasReferralData = true;
    }

    // Check for generic referral parameters
    if (urlParams.has('ref')) {
      const value = urlParams.get('ref');
      console.log('🔗 Found ref:', value);
      referralData.referralKey = value || undefined;
      hasReferralData = true;
    }

    if (urlParams.has('referral')) {
      referralData.referralKey = urlParams.get('referral') || undefined;
      hasReferralData = true;
    }

    if (urlParams.has('partner')) {
      referralData.partnerId = urlParams.get('partner') || undefined;
      hasReferralData = true;
    }

    // Capture UTM parameters for additional attribution
    if (urlParams.has('utm_source')) {
      referralData.source = urlParams.get('utm_source') || undefined;
      hasReferralData = true;
    }

    if (urlParams.has('utm_medium')) {
      referralData.medium = urlParams.get('utm_medium') || undefined;
      hasReferralData = true;
    }

    if (urlParams.has('utm_campaign')) {
      referralData.campaign = urlParams.get('utm_campaign') || undefined;
      hasReferralData = true;
    }

    if (hasReferralData) {
      referralData.timestamp = new Date().toISOString();
      this.storeReferralData(referralData);
      console.log('🔗 Referral data captured:', referralData);
      return referralData;
    }

    return null;
  }

  /**
   * Store referral data in localStorage with expiration
   */
  private storeReferralData(data: ReferralData): void {
    try {
      const expiryDate = new Date();
      expiryDate.setDate(expiryDate.getDate() + this.EXPIRY_DAYS);

      const storageData = {
        referralData: data,
        expiresAt: expiryDate.toISOString()
      };

      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(storageData));
    } catch (error) {
      console.warn('Failed to store referral data:', error);
    }
  }

  /**
   * Retrieve stored referral data (if not expired)
   */
  getStoredReferralData(): ReferralData | null {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) return null;

      const { referralData, expiresAt } = JSON.parse(stored);
      
      // Check if data has expired
      if (new Date() > new Date(expiresAt)) {
        this.clearReferralData();
        return null;
      }

      return referralData;
    } catch (error) {
      console.warn('Failed to retrieve referral data:', error);
      return null;
    }
  }

  /**
   * Clear stored referral data
   */
  clearReferralData(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      console.warn('Failed to clear referral data:', error);
    }
  }

  /**
   * Get referral data for signup attribution
   * Returns stored data or captures new data from URL
   */
  getReferralDataForSignup(): ReferralData | null {
    // First try to get stored referral data
    let referralData = this.getStoredReferralData();
    
    // If no stored data, try to capture from current URL
    if (!referralData) {
      referralData = this.captureReferralData();
    }

    return referralData;
  }

  /**
   * Initialize referral tracking on page load
   * Should be called when the app starts
   */
  initialize(): void {
    console.log('🔗 Initializing referral tracking...');
    console.log('🔗 Current URL:', window.location.href);
    console.log('🔗 URL search params:', window.location.search);

    // Capture referral data from URL if present
    const referralData = this.captureReferralData();

    if (referralData) {
      console.log('🔗 Referral tracking initialized with data:', referralData);
    } else {
      console.log('🔗 No referral parameters found in URL');

      // Check if we have stored referral data
      const stored = this.getStoredReferralData();
      if (stored) {
        console.log('🔗 Found stored referral data:', stored);
      } else {
        console.log('🔗 No stored referral data found');
      }
    }
  }
}

// Export singleton instance
export const referralTracker = new ReferralTracker();
export default referralTracker;
